// The company's ordered buildings on a map, for the dashboard: the same base as the coverage
// map (CouncilMap: ONS boundaries, no basemap tiles), the company's districts shaded, and a
// lime pin per building at its postcode's centre (stored on the order; our own open data).
// Hovering a pin opens a box with the building's details and "Show in My buildings", which
// opens its building page. The box stays open while the pointer is on it.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { OwnedBuilding } from "../api";
import { exclusivityText } from "../pages/MyBuildingsPage";

const STATUS: Record<string, string> = { ordered: "Ordered", "in-progress": "Report in progress", completed: "Report ready", failed: "Released" };

/** CSS custom property value (as CouncilMap). */
function token(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

const esc = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

// An upright lime pin with a white edge and a navy dot, its point on the location. Drawn
// as SVG: Leaflet positions markers with a CSS transform, so a rotated div would lose its rotation.
const PIN = L.divIcon({
  className: "dmap-pin",
  html:
    '<svg viewBox="0 0 28 38" width="28" height="38" aria-hidden="true">' +
    '<path d="M14 1C7 1 1.5 6.4 1.5 13.3 1.5 22.5 14 37 14 37S26.5 22.5 26.5 13.3C26.5 6.4 21 1 14 1Z" fill="#E1EE2B" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="14" cy="13.5" r="4.5" fill="#202937"/></svg>',
  iconSize: [28, 38],
  iconAnchor: [14, 37],
  popupAnchor: [0, -34],
});

// The map's height: down to the bottom of the window, never shorter than this
const MIN_HEIGHT = 360;

export function BuildingsMap({ buildings, districts }: { buildings: OwnedBuilding[]; districts: string[] }) {
  const el = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const go = useRef(navigate);
  go.current = navigate;

  useEffect(() => {
    if (!el.current) return;
    // Fill down to the bottom of the browser window (less a margin), at least MIN_HEIGHT tall;
    // measured from the map's place on the page, so it's the same whether or not it's scrolled
    const canvas = el.current;
    const fill = () => {
      // Below the map: whatever the card adds under it (its padding and border, measured), then
      // the same gap as between cards (the card's own top margin, read from the page's styles)
      const card = canvas.parentElement;
      const gap = (card && parseFloat(getComputedStyle(card).marginTop)) || 16;
      const below = card ? card.getBoundingClientRect().bottom - canvas.getBoundingClientRect().bottom : 0;
      const top = canvas.getBoundingClientRect().top + window.scrollY;
      canvas.style.height = `${Math.max(MIN_HEIGHT, window.innerHeight - top - below - gap)}px`;
    };
    fill();
    const m = L.map(el.current, { zoomControl: true, minZoom: 5, maxZoom: 13, zoomSnap: 0.25, scrollWheelZoom: false }).setView([52.6, -1.6], 6);
    m.attributionControl.setPrefix(false);
    m.attributionControl.addAttribution("Boundaries: ONS, OGL v3.0. Contains OS data © Crown copyright 2026");
    let cancelled = false; // StrictMode mounts twice
    const placed = buildings.filter((b) => b.property.location && b.status !== "failed");

    Promise.all([fetch("/maps/uk-context.geojson").then((r) => r.json()).catch(() => null), fetch("/maps/councils.geojson").then((r) => r.json())])
      .then(([context, councils]) => {
        if (cancelled) return;
        if (context)
          L.geoJSON(context, {
            interactive: false,
            style: () => ({ weight: 1, color: token("--brand-border-strong", "#9a9a9a"), fillColor: token("--brand-border", "#e5e4e2"), fillOpacity: 0.7 }),
          }).addTo(m);
        // Every district faint; the company's own shaded navy, as on Coverage, with its name
        const own: L.Layer[] = [];
        const geo = L.geoJSON(councils, {
          interactive: false,
          onEachFeature: (f, layer) => {
            if (!districts.includes(f.properties.code)) return;
            own.push(layer);
            (layer as L.Path).bindTooltip(esc(f.properties.name), { permanent: true, direction: "center", className: "dmap-label", interactive: false });
          },
          style: (f) => {
            // The company's districts: a black outline, drawn over the neighbours' grey edges
            const mine = districts.includes(f?.properties.code);
            return {
              weight: mine ? 2 : 1,
              color: mine ? "#000000" : token("--brand-text-tertiary", "#8e959e"),
              fillColor: token("--brand-primary", "#202937"),
              fillOpacity: mine ? 0.18 : 0.04,
            };
          },
        }).addTo(m);
        own.forEach((l) => (l as L.Path).bringToFront());

        const markers = placed.map((b) => {
          const p = b.property;
          const box = `
            <div class="dmap-pop">
              <b>${esc(p.name)}</b>
              <div class="muted">${esc([p.buildingType, p.sizeLabel].filter(Boolean).join(" · "))}</div>
              <div>${esc([p.town, p.postcode].filter(Boolean).join(", "))} · ${esc(p.district)}</div>
              <div class="dmap-pop-status">${esc(STATUS[b.status] ?? b.status)} · ${esc(exclusivityText(b))}</div>
              <a href="/properties/${encodeURIComponent(b.orderId)}" data-order="${esc(b.orderId)}" class="dmap-pop-link">Show in My properties →</a>
            </div>`;
          const mk = L.marker([p.location!.lat, p.location!.lng], { icon: PIN, title: p.name, keyboard: true }).bindPopup(box, {
            closeButton: false,
            autoPan: true,
            className: "dmap-popup",
          });
          // Open on hover; close shortly after the pointer leaves both the pin and the box
          let timer = 0;
          const keep = () => window.clearTimeout(timer);
          const leave = () => {
            timer = window.setTimeout(() => mk.closePopup(), 250);
          };
          mk.on("mouseover", () => {
            keep();
            mk.openPopup();
          });
          mk.on("mouseout", leave);
          mk.on("popupopen", (e) => {
            const node = (e as L.PopupEvent).popup.getElement();
            if (!node) return;
            node.onmouseenter = keep;
            node.onmouseleave = leave;
            // In-app navigation for the link (no full page reload)
            const a = node.querySelector<HTMLAnchorElement>("a[data-order]");
            if (a)
              a.onclick = (ev) => {
                ev.preventDefault();
                go.current(`/properties/${encodeURIComponent(a.dataset.order!)}`);
              };
          });
          // Clicking the pin (or Enter on it) goes straight to the building
          mk.on("click", () => go.current(`/properties/${encodeURIComponent(b.orderId)}`));
          return mk.addTo(m);
        });

        // Fit the pins with a generous margin, whatever districts are covered (one pin: a
        // town-level view around it); no pins: the company's districts, else England and Wales
        if (markers.length) m.fitBounds(L.featureGroup(markers).getBounds().pad(0.35), { padding: [40, 40], maxZoom: 12 });
        else if (own.length) m.fitBounds(L.featureGroup(own).getBounds(), { padding: [12, 12] });
        else m.fitBounds(geo.getBounds(), { padding: [6, 6] });
      })
      .catch(() => null);

    // Keep filling the window as it's resized (Leaflet redraws for the new size)
    const onResize = () => {
      fill();
      m.invalidateSize();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      m.remove();
    };
  }, [buildings, districts]);

  return <div ref={el} className="dmap-canvas" role="application" aria-label="Map of your properties" />;
}
